import { type ReactElement } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  CALENDAR_BACK,
  CALENDAR_FORWARD,
  calendarWindow,
  shiftDay,
  type IsoDate,
  type MapResponse,
  type Reservation,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { cellsFor, labelOf, type CalendarCell } from '@/lib/calendar';
import { dayMonthOf, weekdayOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const BLOCK: Readonly<Record<CalendarCell['kind'], string>> = {
  empty: '',
  whole: 'rounded-lg',
  start: 'rounded-s-lg',
  mid: '',
  end: 'rounded-e-lg',
};

// Fourteen days travelling with the selected one, a row per spot. §15
export function CalendarView({
  map,
  bookings,
  onPickDay,
  onReserve,
}: {
  map: MapResponse;
  bookings: readonly Reservation[];
  onPickDay: (date: IsoDate) => void;
  onReserve: ((spotId: string, date: IsoDate) => void) | null;
}): ReactElement {
  const { t, language } = useTranslation();
  const days = calendarWindow(map.date, map.today);
  const page = CALENDAR_BACK + CALENDAR_FORWARD + 1;
  const todayShown = days.some((day) => day.date === map.today);

  return (
    <section className="flex flex-col gap-2 px-2 pb-6 sm:px-3">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={() => onPickDay(shiftDay(map.date, -page))}
        >
          <ChevronLeft className="size-4" aria-hidden />
          {t('daysBack')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={() => onPickDay(shiftDay(map.date, page))}
        >
          {t('daysForward')}
          <ChevronRight className="size-4" aria-hidden />
        </Button>
        {/* A window paged far from today needs its own way back to it. §8 */}
        {!todayShown && (
          <Button size="sm" className="shrink-0" onClick={() => onPickDay(map.today)}>
            {t('today')}
          </Button>
        )}
        <p className="min-w-0 truncate text-xs text-muted-foreground">{t('calendarHint')}</p>
      </div>
      {/* Bounded height, so the day headers have a scrollport to stick to. */}
      <div className="max-h-[70vh] overflow-auto rounded-2xl border">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {/* safe-area: sticks to the scrollport above, never to the screen. */}
              <th className="sticky start-0 top-0 z-3 bg-card p-2 text-start">{t('spotLabel')}</th>
              {days.map((day) => (
                // safe-area: sticks to the scrollport above, never to the screen.
                <th
                  key={day.date}
                  className={cn(
                    'sticky top-0 z-2 bg-card p-1',
                    day.mode === 'past' && 'opacity-50',
                    day.date === map.today && 'bg-primary/10',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onPickDay(day.date)}
                    className={cn(
                      'w-full rounded-lg px-1 py-1 leading-tight font-semibold',
                      day.date === map.date && 'bg-foreground text-background',
                    )}
                  >
                    {day.date === map.today ? t('today') : weekdayOf(day.date, language)}
                    <br />
                    <span className="font-normal">{dayMonthOf(day.date, language)}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {map.spots.map((spot) => (
              <tr key={spot.id} className="border-t">
                <th className="sticky start-0 z-1 bg-card p-2 text-start font-bold">
                  #{spot.number}
                </th>
                {cellsFor(
                  spot.id,
                  bookings,
                  days.map((day) => day.date),
                ).map((cell, index) => {
                  const past = days[index]?.mode === 'past';
                  return (
                    <td
                      key={cell.date}
                      data-testid={`cell-${spot.number}-${cell.date}`}
                      className={cn(
                        'h-8 p-0.5',
                        past && 'bg-secondary/60',
                        cell.date === map.today && 'bg-primary/5',
                      )}
                    >
                      {cell.kind === 'empty' ? (
                        // §4: no offer to book where the reader may not book.
                        <button
                          type="button"
                          disabled={past || onReserve === null}
                          aria-label={`${t('reserve')} #${spot.number} ${cell.date}`}
                          onClick={() => onReserve?.(spot.id, cell.date)}
                          className="h-7 w-full rounded-lg disabled:cursor-not-allowed"
                        />
                      ) : (
                        <span
                          className={cn(
                            'flex h-7 items-center overflow-hidden px-1 font-semibold whitespace-nowrap',
                            'bg-amber-200 text-amber-950',
                            BLOCK[cell.kind],
                          )}
                        >
                          {labelOf(cell)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
